import { Controller } from "@hotwired/stimulus"

// Creator insights page. Fetches from /creator_insights/* (CreatorInsightsService) and draws:
// four headline numbers, one trend chart with metric tabs, a sortable story list and a weekly
// publishing strip. One period switch drives everything except the strip.
//
// Chart.js comes from a CDN script tag in the view; if it fails to load the chart area says so and
// everything else still works. Colours are read from the theme's CSS variables on every draw, so a
// light/dark switch redraws the chart in the right palette.
const NUMBER = new Intl.NumberFormat()

export default class extends Controller {
  static targets = ["periodButton", "modeButton", "kpi", "metricTab", "chart", "chartEmpty", "chartCaption",
                    "sortButton", "stories", "weeks", "streak"]
  static values = { period: Number, mode: String }

  connect() {
    this.metric = "reads"
    this.sort = "views"
    this.rows = []
    this.observeTheme()
    this.onResize = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => this.drawChart(), 150)
    }
    window.addEventListener("resize", this.onResize)
    this.loadAll()
    this.loadWeeks()
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
    this.themeObserver?.disconnect()
    this.chartInstance?.destroy()
  }

  // ------------------------------------------------------------------- controls

  setPeriod(event) {
    this.periodValue = Number(event.currentTarget.dataset.period)
    this.press(this.periodButtonTargets, event.currentTarget)
    this.loadAll()
  }

  setMode(event) {
    this.modeValue = event.currentTarget.dataset.mode
    this.press(this.modeButtonTargets, event.currentTarget)
    this.loadAll()
  }

  setMetric(event) {
    this.metric = event.currentTarget.dataset.metric
    this.metricTabTargets.forEach((tab) => {
      const on = tab === event.currentTarget
      tab.classList.toggle("is-active", on)
      tab.setAttribute("aria-selected", String(on))
    })
    this.loadChart()
  }

  setSort(event) {
    this.sort = event.currentTarget.dataset.sort
    this.press(this.sortButtonTargets, event.currentTarget)
    this.renderStories()
  }

  press(buttons, active) {
    buttons.forEach((b) => {
      b.classList.toggle("is-active", b === active)
      b.setAttribute("aria-pressed", String(b === active))
    })
  }

  loadAll() {
    this.loadSummary()
    this.loadChart()
    this.loadStories()
  }

  // ---------------------------------------------------------------- headline row

  async loadSummary() {
    this.kpiTargets.forEach((k) => k.classList.add("is-loading"))
    const days = this.periodValue
    const unique = this.modeValue === "unique"
    const [s, rate] = await Promise.all([
      this.fetchJson(`/creator_insights/summary?days=${days}`),
      unique ? this.fetchJson(`/creator_insights/engagement_rate?period=${days}&view_mode=unique`) : null
    ])
    if (!s) return
    const window = `previous ${days} days`

    this.setKpi("reads", {
      value: NUMBER.format(unique ? s.total_unique_reads : s.total_reads),
      delta: unique ? s.change.total_unique_reads : s.change.total_reads,
      deltaTitle: `vs ${window}`,
      sub: `${NUMBER.format(unique ? s.all_time_unique_reads : s.all_time_reads)} all time`
    })
    // A rate over zero views isn't 0%, it's undefined; say so rather than print a misleading figure.
    const viewsInPeriod = unique ? s.total_unique_reads : s.total_reads
    this.setKpi("rate", {
      value: viewsInPeriod > 0 ? this.formatRate(unique ? rate?.engagement_rate : s.engagement_rate) : "\u2013",
      unitSplit: true,
      delta: s.change.total_engagement,
      deltaTitle: `reader actions vs ${window}`,
      sub: `${NUMBER.format(s.total_engagement)} reader ${s.total_engagement === 1 ? "action" : "actions"}`
    })
    this.setKpi("followers", {
      value: NUMBER.format(s.total_followers),
      delta: s.change.followers_gained,
      deltaTitle: `new followers vs ${window}`,
      sub: s.followers_gained > 0 ? `+${NUMBER.format(s.followers_gained)} this period` : "none new this period"
    })
    this.setKpi("stories", {
      value: NUMBER.format(s.total_stories_published),
      delta: null,
      sub: `${s.unique_reads_per_story} unique reads each, on average`
    })
  }

  setKpi(key, { value, delta, deltaTitle, sub, unitSplit }) {
    const el = this.kpiTargets.find((k) => k.dataset.key === key)
    if (!el) return
    const valueEl = el.querySelector("[data-role=value]")
    // "3.2 per reader": the number at headline size, the unit small beside it.
    const [num, ...unit] = unitSplit ? value.split(" ") : [value]
    valueEl.textContent = num
    if (unit.length) valueEl.append(this.el("span", "kpi__unit", unit.join(" ")))
    el.querySelector("[data-role=sub]").textContent = sub
    const d = el.querySelector("[data-role=delta]")
    // No baseline means no honest percentage; the service sends null and we show nothing.
    if (delta === null || delta === undefined) {
      d.hidden = true
    } else {
      d.hidden = false
      d.dataset.trend = delta > 0 ? "up" : delta < 0 ? "down" : "flat"
      d.textContent = `${delta > 0 ? "↑" : delta < 0 ? "↓" : "→"} ${Math.abs(delta)}%`
      d.title = deltaTitle || ""
    }
    el.classList.remove("is-loading")
  }

  // ---------------------------------------------------------------------- chart

  async loadChart() {
    const days = this.periodValue
    const metric = this.metric
    const url = {
      reads: `/creator_insights/reads?days=${days}&view_mode=${this.modeValue}`,
      engagement: `/creator_insights/engagement_series?days=${days}`,
      followers: `/creator_insights/followers?days=${days}`
    }[metric]
    const data = await this.fetchJson(url)
    if (!data || metric !== this.metric) return

    const span = days === 365 ? "the last year" : `the last ${days} days`
    let values = data.values
    let caption
    let empty = null
    if (metric === "followers") {
      values = data.cumulative
      const now = values[values.length - 1] || 0
      caption = `${NUMBER.format(data.total)} new in ${span} · ${NUMBER.format(now)} total`
      if (now === 0) empty = "No followers yet. They arrive with the next story people share."
    } else {
      const noun = metric === "reads" ? (this.modeValue === "unique" ? "unique readers" : "reads") : "reader actions"
      caption = `${NUMBER.format(data.total)} ${noun} in ${span}`
      if (data.total === 0) {
        empty = metric === "reads"
          ? `No reads in ${span}. Publishing or sharing a story is what moves this line.`
          : `No comments, likes or saves in ${span}.`
      }
    }

    this.chartCaptionTarget.textContent = caption
    this.series = { labels: data.labels, values, metric, empty }
    this.drawChart()
  }

  drawChart() {
    if (!this.series || !this.hasChartTarget) return
    const { labels, values, metric, empty } = this.series
    this.chartInstance?.destroy()
    this.chartInstance = null

    if (empty) return this.showChartMessage(empty)
    if (!window.Chart) return this.showChartMessage("The chart couldn't load. Check your connection and reload the page.")
    this.chartEmptyTarget.hidden = true
    this.chartTarget.hidden = false

    const css = getComputedStyle(document.documentElement)
    const ink = css.getPropertyValue("--ink").trim() || "#1e1c1a"
    const accent = css.getPropertyValue("--accent").trim() || "#2f7f7b"
    const paper = css.getPropertyValue("--surface").trim() || "#fff"
    const ctx = this.chartTarget.getContext("2d")
    const height = this.chartTarget.parentElement.clientHeight || 280
    const fill = ctx.createLinearGradient(0, 0, 0, height)
    fill.addColorStop(0, this.alpha(accent, 0.22))
    fill.addColorStop(1, this.alpha(accent, 0))
    // Tick density comes from the plot width, not a fixed "every Nth label" rule.
    const maxTicks = Math.max(3, Math.min(10, Math.floor(this.chartTarget.parentElement.clientWidth / 90)))
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.chartInstance = new window.Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [{
          data: values,
          borderColor: accent,
          backgroundColor: fill,
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: accent,
          pointHoverBorderColor: paper,
          pointHoverBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: reduce ? false : { duration: 600, easing: "easeOutQuart" },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: ink,
            titleColor: paper,
            bodyColor: paper,
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: { label: (item) => `${NUMBER.format(item.parsed.y)} ${this.unitFor(metric, item.parsed.y)}` }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: this.alpha(ink, 0.5), maxTicksLimit: maxTicks, autoSkip: true, maxRotation: 0, font: { size: 11 } }
          },
          y: {
            beginAtZero: true,
            grid: { color: this.alpha(ink, 0.07) },
            border: { display: false },
            ticks: { color: this.alpha(ink, 0.5), precision: 0, maxTicksLimit: 5, font: { size: 11 } }
          }
        }
      }
    })
  }

  unitFor(metric, n) {
    if (metric === "followers") return n === 1 ? "follower" : "followers"
    if (metric === "engagement") return n === 1 ? "action" : "actions"
    return this.modeValue === "unique" ? (n === 1 ? "reader" : "readers") : (n === 1 ? "read" : "reads")
  }

  showChartMessage(text) {
    this.chartTarget.hidden = true
    this.chartEmptyTarget.textContent = text
    this.chartEmptyTarget.hidden = false
  }

  // -------------------------------------------------------------------- stories

  async loadStories() {
    const rows = await this.fetchJson(`/creator_insights/top_stories?period=${this.periodValue}&view_mode=${this.modeValue}&limit=50`)
    if (!rows) return
    this.rows = rows
    this.renderStories()
  }

  renderStories() {
    const box = this.storiesTarget
    box.replaceChildren()
    if (this.rows.length === 0) {
      const p = document.createElement("p")
      p.className = "insights-empty insights-empty--inline"
      p.textContent = "Publish a story and its numbers will show up here."
      box.append(p)
      return
    }

    const key = this.sort
    const rows = [...this.rows].sort((a, b) =>
      key === "published_at" ? new Date(b.published_at) - new Date(a.published_at) : (b[key] || 0) - (a[key] || 0))
    const maxEngagement = Math.max(1, ...rows.map((r) => r.engagement))
    const unique = this.modeValue === "unique"

    const head = this.el("div", "story-stats__row story-stats__row--head")
    head.append(this.el("span", "", "Story"), this.el("span", "is-num", unique ? "Readers" : "Reads"),
                this.el("span", "", "Engagement"), this.el("span", "is-num", "Rate"))
    box.append(head)

    rows.forEach((r) => {
      const row = this.el("div", "story-stats__row")
      const who = this.el("div", "story-stats__story")
      const link = this.el("a", "story-stats__title", r.title)
      link.href = `/stories/${encodeURIComponent(r.slug)}`
      const meta = [new Date(r.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })]
      const parts = [[r.comments, "comment"], [r.reactions, "like"], [r.saves, "save"]].filter(([n]) => n > 0)
      if (parts.length) meta.push(parts.map(([n, w]) => `${n} ${w}${n === 1 ? "" : "s"}`).join(", "))
      if (r.appreciation > 0) meta.push(`appreciation ${r.appreciation}`)
      who.append(link, this.el("span", "story-stats__meta", meta.join(" · ")))

      const reads = this.el("span", "is-num story-stats__reads", NUMBER.format(r.views))
      const eng = this.el("div", "story-stats__eng")
      const track = this.el("span", "story-stats__track")
      const bar = this.el("span", "story-stats__bar")
      bar.style.setProperty("--w", `${(r.engagement / maxEngagement) * 100}%`)
      track.append(bar)
      eng.append(track, this.el("span", "is-num", NUMBER.format(r.engagement)))
      const rate = this.el("span", "is-num story-stats__rate", r.views > 0 ? this.formatRate(r.engagement_rate) : "–")

      row.append(who, reads, eng, rate)
      box.append(row)
    })
  }

  // ---------------------------------------------------------------------- weeks

  async loadWeeks() {
    const data = await this.fetchJson("/creator_insights/weeks")
    if (!data) return
    const box = this.weeksTarget
    box.replaceChildren()
    let lastMonth = null
    data.weeks.forEach((w) => {
      const col = this.el("div", "weeks__col")
      const cell = this.el("span", "weeks__cell")
      cell.dataset.level = String(Math.min(w.count, 3))
      cell.title = `Week of ${w.label}: ${w.count} ${w.count === 1 ? "story" : "stories"}`
      const month = w.label.split(" ")[0]
      const label = this.el("span", "weeks__month", month !== lastMonth ? month : "")
      lastMonth = month
      col.append(cell, label)
      box.append(col)
    })

    const s = data.streak
    const published = data.weeks.reduce((n, w) => n + w.count, 0)
    this.streakTarget.textContent = s
      ? `${s.current}-week streak · best ${s.longest} · ${s.this_week}/${s.goal} this week`
      : `${published} ${published === 1 ? "story" : "stories"} in the last 26 weeks`
  }

  // -------------------------------------------------------------------- helpers

  // Engagement counts every comment, like and save, so one keen reader can push the ratio past 1.
  // "315%" reads like a bug; past 1 it's shown as actions per read instead.
  formatRate(rate) {
    const r = Number(rate) || 0
    if (r < 1) return `${(r * 100).toFixed(1)}%`
    return `${r.toFixed(1)} per ${this.modeValue === "unique" ? "reader" : "read"}`
  }

  async fetchJson(url) {
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      return res.ok ? await res.json() : null
    } catch (_) {
      return null
    }
  }

  el(tag, className, text) {
    const node = document.createElement(tag)
    if (className) node.className = className
    if (text !== undefined) node.textContent = text
    return node
  }

  // Canvas gradients reject color-mix(), so theme colours (hex variables) become plain rgba().
  alpha(color, a) {
    let hex = color.replace("#", "").trim()
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("")
    if (!/^[0-9a-f]{6}$/i.test(hex)) return color
    const n = parseInt(hex, 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }

  observeTheme() {
    this.themeObserver = new MutationObserver(() => this.drawChart())
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
  }
}
